import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  CsvFile,
  DateSelector, MultipleSelect,
  PriceConvertor, SearchInput,
  TabButtons,
  UserInputsDropdown,
} from 'components';
import { PermissionsList, reserveReleasedList, RolePermission, SavePage, SaveParams, useGlobalStyles } from 'utils';
import { payableCsv, tabEnums, tabParams } from './constants';
import { TotalItems } from '../receivables/core/totalItems';
import { carrierActions, loadActions, mcActions, officeActions } from 'store';
import { OpenPayables } from './openPayables';
import { PendingPayableBatches } from './pendingPayableBatches';
import { PaidPayableBatches } from './paidPayableBatches';
import { ApprovedPayableBatches } from './approvedPayableBatches';

export const Payables = () => {
  const { payableInfo, carriersList, carriersListForSelect, mcsList, officesList } = useSelector((state) => ({
    payableInfo: state.loads.payableInfo,
    carriersList: state.carriers.carriersList,
    carriersListForSelect: state.carriers.carriersListForSelect,
    mcsList: state.mcs.mcsList,
    officesList: state.offices.officesList,
  }));
  const dispatch = useDispatch();
  const history = useHistory();
  const intervalRef = useRef(null);
  const globalClasses = useGlobalStyles();
  const info = history?.location?.state;
  const payableParams = {
    ...payableCsv,
    carrier: info?.carrier,
  };

  useEffect(() => {
    // dispatch(carrierActions.getCarriers({ statuses: ['ACTIVE'] }));
    dispatch(carrierActions.getCarriersForSelect());
    dispatch(mcActions.getMcs({ status: 'ACTIVE' }));
    dispatch(officeActions.getOffices({ status: 'ACTIVE' }));
  }, []);

  // useEffect(() => {
  //   if (!info?.viewType || info?.viewType === tabEnums.OPEN) {
  //     dispatch(carrierActions.getCarriersForSelect({ statuses: ['ACTIVE'] }));
  //   } else {
  //     dispatch(carrierActions.getCarriersForSelect());
  //   }
  // }, [info?.viewType]);


  useEffect(() => {
    if (info?.viewType !== tabEnums.PAID) {
      handleGet('loading');

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        handleGet('noLoad');
      }, 30000);
      return () => clearInterval(intervalRef.current);
    }
  }, [info]);

  const renderParams = () => {
    const filteredInfo = {
      ...info,
      isPendingPayable: info?.viewType === tabEnums.PENDING,
    };
    info?.viewType === tabEnums.PENDING ? filteredInfo.isPendingPayable = true : delete filteredInfo.isPendingPayable;
    info?.viewType === tabEnums.APPROVED ? filteredInfo.isApprovedPayable = true : delete filteredInfo.isApprovedPayable;

    info?.mcs ? filteredInfo.mcs = info.mcs?.map((mc) => mc?.id) : delete filteredInfo?.mcs;
    info?.offices ? filteredInfo.offices = info.offices?.map((office) => office?.id) : delete filteredInfo?.offices;
    delete filteredInfo.page;
    delete filteredInfo.activeTab;
    delete filteredInfo.viewType;
    delete filteredInfo.skip;
    delete filteredInfo.info;
    return filteredInfo;
  };

  const handleChange = (value, name) => {
    const params = {
      ...info,
    };
    params.skip = 0;
    params.limit = 100;
    params.page = 1;
    if (value) {
      params[name] = value.id;
    } else {
      delete params[name];
    }
    SavePage(history, info, { ...params });
  };

  const handleGet = () => {
    dispatch(loadActions.getPayableInfo(renderParams()));
  };


  const selectAuthority = (item) => {
    SaveParams(history, { ...info, mcs: item });
  };

  const selectOffice = (item) => {
    SaveParams(history, { ...info, offices: item });
  };

  return (
    <div className="payables-page-wrapper">
      <div className="payables-page-tabs-filters-block">
        <TabButtons tabParams={tabParams} resetParams={true} />

        <div className="align-center" style={{ width: '100%', gap: 16 }}>
          <DateSelector
            startName={'from'}
            endName={'to'}
          />
          {
            <MultipleSelect
              selectedList={info?.mcs}
              permissionsList={mcsList?.mcs || []}
              handleChange={selectAuthority}
              placeholder={'All Authority'}
              renderName={(item) => item?.name}
              smallSizeFullWidth
            />
          }

          {
            <MultipleSelect
              selectedList={info?.offices}
              permissionsList={officesList?.offices || []}
              handleChange={selectOffice}
              placeholder={'All Offices'}
              renderName={(item) => item?.name}
              smallSizeFullWidth
            />
          }

          {
            <UserInputsDropdown
              dropdownClassName="payables-page-carrier-select"
              dropdownOptions={carriersListForSelect?.carriers}
              onPass={(option) => handleChange(option, 'carrier')}
              selected={info?.carrier ? info?.carrier : ''}
              noText={'All Vendors'}
              firstText={'companyName'}
            />
          }

          {(!info?.viewType || info?.viewType === tabEnums.OPEN) &&
            <UserInputsDropdown
              dropdownClassName="payables-page-carrier-select"
              dropdownOptions={reserveReleasedList}
              onPass={(option) => handleChange(option, 'reserveState')}
              selected={info?.reserveState ? info?.reserveState : ''}
              noText={'All Loads'}
              firstText={'name'}
            />
          }

          {(info?.viewType && info?.viewType !== tabEnums.OPEN) &&
            <SearchInput
              className="payables-page-search-input"
              name="loadId"
              saveParams={info}
              placeholder="Search for Load ID"
            />
          }

          {((!info?.viewType || info?.viewType === tabEnums.OPEN) && RolePermission([PermissionsList?.ALL_ACCESS?.code, PermissionsList?.EXPORT_PAYABLE_CSV?.code])) &&
            <CsvFile params={payableParams} />
          }
        </div>
      </div>

      {info?.viewType !== tabEnums.PAID &&
        <div className={globalClasses.totalWrapper}>
          <div className={globalClasses.centerItem}>
            <p className={globalClasses.totalTitle}>Totals</p>
            <div className={globalClasses.receivablesTotalsBox}>
              <TotalItems
                text={<PriceConvertor price={payableInfo?.current?.amount} />}
                count={` (${payableInfo?.current?.count || 0})`}
                title={'Current:'}
                color={'#00C8511A'}
              />
              <TotalItems
                text={<PriceConvertor price={payableInfo?.dueNow?.amount} />}
                count={` (${payableInfo?.dueNow?.count || 0})`}
                title={'Due Now:'}
                color={'#438AFE1A'}
              />
              <TotalItems
                text={<PriceConvertor price={payableInfo?.pastDue?.amount} />}
                count={` (${payableInfo?.pastDue?.count || 0})`}
                title={'Past Due:'}
                color={'#FFA3301A'}
              />
              <TotalItems
                text={<PriceConvertor price={payableInfo?.urgent?.amount} />}
                title={'Urgent:'}
                count={` (${payableInfo?.urgent?.count || 0})`}
                color={'#D725251A'}
              />
            </div>

          </div>
          <div>
            <TotalItems
              text={<PriceConvertor price={payableInfo?.grandTotal?.amount} />}
              count={` (${payableInfo?.grandTotal?.count || 0})`}
              title={'Total: '}
              color={'#E6ECF380'}
              styles={{ marginRight: 0 }}
            />
          </div>
        </div>
      }

      {(!info?.viewType || info?.viewType === tabEnums.OPEN) &&
        <OpenPayables getTotals={handleGet} />
      }

      {(info?.viewType === tabEnums.PENDING) &&
        <PendingPayableBatches getTotals={handleGet} />
      }

      {(info?.viewType === tabEnums.APPROVED) &&
        <ApprovedPayableBatches getTotals={handleGet} />
      }

      {(info?.viewType === tabEnums.PAID) &&
        <PaidPayableBatches />
      }
    </div>
  );
};

